import React, { useState, useEffect, useCallback } from 'react';
import { getMyRoyalty, claimRoyalty } from '../../utils/api';

const RoyaltyCard = ({ account, onRefresh, f, fBC, vBC, BC, SYM = 'ROBO', readOnly = false }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isClaiming, setIsClaiming] = useState(false);
  const [error, setError] = useState('');
  const [result, setResult] = useState(null);

  const load = useCallback(async () => {
    if (!account) return;
    setLoading(true);
    try { const res = await getMyRoyalty(account); setData(res); } catch { setData(null); }
    setLoading(false);
  }, [account]);

  useEffect(() => { load(); }, [load]);

  const handleClaim = async () => {
    if (readOnly) { setError('Read-only admin view — actions are disabled.'); return; }
    setIsClaiming(true); setError(''); setResult(null);
    try {
      const r = await claimRoyalty({ walletAddress: account });
      setResult(r.message || 'Royalty claim submitted.'); load(); onRefresh && onRefresh();
    } catch (e) { setError(e.response?.data?.error || 'Claim failed'); }
    setIsClaiming(false);
  };

  const statusBadge = (s) => {
    const map = { pending: 'badge-pending', approved: 'badge-approved', rejected: 'badge-rejected' };
    return <span className={`d-status-badge ${map[s] || ''}`}>{s}</span>;
  };
  const tick = (ok) => <span style={{ color: ok ? 'var(--green, #27ae60)' : 'var(--red, #e74c3c)' }}>{ok ? '✓' : '✗'}</span>;
  const fmtDate = (d) => d ? new Date(d).toLocaleString('en-US', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit', hour12: true }) : '—';

  if (loading) return <div className="fade-in card"><p className="d-trade-sub">Loading royalty status…</p></div>;
  if (!data || !data.enabled) return <div className="fade-in card"><h3>Royalty Income System</h3><p className="d-trade-sub">The royalty program is currently disabled.</p></div>;

  const cur = data.current?.qualification || {};
  const curTiers = cur.perTier || [];
  const curQualified = cur.qualifiedTier || 0;

  const cl = data.claimable || {};
  const clQ = cl.qualification || {};
  const clQualified = clQ.qualifiedTier || 0;
  const canClaim = !!cl.canClaim;
  const clDistributed = !!cl.distributed;
  const clReward = cl.rewardAmount || 0;

  const monthName = (key) => {
    if (!key) return '—';
    const [y, m] = String(key).split('-').map(Number);
    return new Date(y, (m || 1) - 1, 1).toLocaleDateString('en-GB', { month: 'long', year: 'numeric' });
  };

  return (
    <div className="fade-in">
      <div className="d-trade card">
        <h3>Royalty Income System</h3>
        <p className="d-trade-sub">
          5% of every purchase feeds the Royalty Pool. Your tier is finalized <strong>per completed month</strong> —
          you claim the previous month, so a mid-month lower tier is never locked in.
          {' '}Members are counted from your <strong>direct referrals</strong>; monthly volume from your <strong>whole team</strong>.
        </p>

        {/* Claimable — last completed month (finalized) */}
        <div className="d-claim-rows" style={{ border: '1px solid var(--accent)', borderRadius: 12, padding: 14, background: 'var(--accent-dim)', overflow: 'hidden' }}>
          <div className="d-claim-line"><span>Claim for month</span><strong>{monthName(cl.periodKey)}</strong></div>
          <div className="d-claim-line d-claim-highlight"><span>Your finalized tier</span><strong className="mono text-accent">{clQualified ? `${clQ.qualifiedTierName} (L${clQualified})` : 'None'}</strong></div>
          {clQualified > 0 && (
            <div className="d-claim-line"><span className="text-3">Self {vBC(clQ.selfInvestment, 0)} · Team {vBC(clQ.teamVolume, 0)} · {clQ.qualifyingMembers} direct members</span></div>
          )}
          {clQualified > 0 && clDistributed && (
            <div className="d-claim-line d-claim-highlight"><span>Your reward (pool share)</span><strong className="mono text-accent">{vBC(clReward, 2)}</strong></div>
          )}
        </div>

        <div className="d-form" style={{ marginTop: 12 }}>
          {cl.alreadyClaimed && <div className="notice notice-warning">You already claimed royalty for {monthName(cl.periodKey)}. Awaiting {SYM} team review.</div>}
          {!clQualified && !cl.alreadyClaimed && <div className="notice notice-warning">You did not qualify for a royalty tier in {monthName(cl.periodKey)}.</div>}
          {clQualified > 0 && !clDistributed && !cl.alreadyClaimed && (
            <div className="notice notice-success">🎉 You qualified for {clQ.qualifiedTierName} in {monthName(cl.periodKey)}! Your reward becomes claimable once the {SYM} team finalizes this month's royalty pool.</div>
          )}
          {error && <div className="notice notice-error">{error}</div>}
          {result && <div className="notice notice-success">{result}</div>}
          <button className="btn btn-primary btn-lg" onClick={handleClaim} disabled={readOnly || isClaiming || !canClaim}>
            {readOnly ? 'Read-only view'
              : isClaiming ? 'Submitting…'
              : canClaim ? `Claim ${fBC(clReward, 2)} ${BC}`
              : cl.alreadyClaimed ? 'Already claimed'
              : (clQualified > 0 && !clDistributed) ? 'Awaiting finalization'
              : 'Nothing to claim'}
          </button>
          <p className="d-claim-note">Your reward is your equal share of this month's royalty pool for your level, finalized by the {SYM} team.</p>
        </div>

        {/* This month — live progress (NOT claimable yet) */}
        <div style={{ marginTop: 22 }}>
          <h4 style={{ margin: '0 0 8px' }}>This month so far <span className="text-3" style={{ fontWeight: 400, fontSize: 12 }}>· {monthName(data.current?.periodKey)} — in progress, claim next month</span></h4>
          <div className="d-claim-rows">
            <div className="d-claim-line"><span>Self-investment (lifetime)</span><strong className="mono">{vBC(cur.selfInvestment, 2)}</strong></div>
            <div className="d-claim-line"><span>Team volume — whole team (this month)</span><strong className="mono">{vBC(cur.teamVolume, 2)}</strong></div>
            <div className="d-claim-line"><span>Direct referrals · Team size</span><strong className="mono">{cur.directCount || 0} · {cur.downlineCount || 0}</strong></div>
            <div className="d-claim-line d-claim-highlight"><span>Tracking tier</span><strong className="mono text-accent">{curQualified ? `${cur.qualifiedTierName} (L${curQualified})` : 'None yet'}</strong></div>
          </div>
          <div className="d-trade-row" style={{ marginTop: '1rem' }}>
            {curTiers.map(t => (
              <div key={t.level} className="card" style={{
                flex: 1, padding: '1rem',
                border: t.level === curQualified ? '2px solid var(--accent)' : '1px solid var(--border)',
                opacity: t.qualified ? 1 : 0.85,
              }}>
                <h4 style={{ margin: '0 0 .5rem' }}>L{t.level} — {t.name} {t.qualified && <span className="d-status-badge badge-approved">On track</span>}</h4>
                <div className="d-claim-line"><span>{tick(t.selfOk)} Self ≥ {vBC(t.selfInvestmentMin, 0)}</span></div>
                <div className="d-claim-line"><span>{tick(t.membersOk)} {t.qualifyingMembers}/{t.minMembers} direct @ ≥ {vBC(t.perMemberMin, 0)}/mo</span></div>
                <div className="d-claim-line"><span>{tick(t.volumeOk)} Whole-team ≥ {vBC(t.monthlyTeamMin, 0)} this month</span></div>
              </div>
            ))}
          </div>
        </div>

        {/* History */}
        {data.claims && data.claims.length > 0 && (
          <div className="d-claim-history"><h4>Royalty Claims</h4>
            <div className="table-wrap"><table className="table">
              <thead><tr><th>Period</th><th>Tier</th><th>Reward</th><th>Status</th><th>Info</th></tr></thead>
              <tbody>{data.claims.map(c => (
                <tr key={c._id}>
                  <td>{c.periodKey}</td>
                  <td>{c.tierName} (L{c.tier})</td>
                  <td className="mono">
                    {c.rewardType === 'income' ? `${fBC(c.rewardAmount, 2)} ${c.currency || BC}` : (c.rewardType === 'item' ? (c.rewardNote || 'Item') : '—')}
                  </td>
                  <td>{statusBadge(c.status)}</td>
                  <td className="text-3">
                    {c.status === 'approved' && c.processedBy && `By ${c.processedBy}`}
                    {c.status === 'rejected' && c.rejectionReason}
                    {c.status === 'pending' && 'Awaiting review'}
                  </td>
                </tr>
              ))}</tbody>
            </table></div>
          </div>
        )}
      </div>
    </div>
  );
};

export default RoyaltyCard;